import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import mixpanel, {
  trackEvent,
  trackCategoryViewed,
  trackMenuItemViewed,
  trackBannerClicked,
  trackSearchUsed,
  trackLanguageChanged,
  trackItemAddedToCart,
  trackItemRemovedFromCart,
  trackCartViewed,
  trackCheckoutStarted,
  trackPaymentSelected,
  trackOrderSubmitted,
  trackOrderPaymentSucceeded,
  trackOrderPaymentFailed,
  trackOrderStatusViewed,
  trackTaxiOrderTrackingOpened,
  trackExternalServiceCategoryViewed,
  trackExternalServiceOrderSubmitted,
  trackAPIError,
  trackPaymentGatewayError,
} from '../utils/mixpanel';

/**
 * Custom hook for Mixpanel tracking
 *
 * Usage:
 * const { track } = useMixpanel();
 * track.categoryViewed({ category_id: 1, category_name: 'Food', category_type: 'restaurant' });
 */
const useMixpanel = () => {
  const location = useLocation();

  // Extract room_uuid from current path
  const roomUuid = useMemo(() => {
    const pathParts = location.pathname.split('/');
    return pathParts[1] || '';
  }, [location.pathname]);

  // Get currency from localStorage
  const currency = localStorage.getItem('currency') || 'AED';

  // Generic track function with automatic room_uuid injection
  const track = useCallback(
    (eventName, properties = {}) => {
      trackEvent(eventName, {
        ...properties,
        current_path: location.pathname,
      });
    },
    [location.pathname]
  );

  // Memoized tracking methods
  const trackingMethods = useMemo(
    () => ({
      // Generic tracking
      event: track,

      // Navigation & Browsing
      categoryViewed: (props) =>
        trackCategoryViewed({
          ...props,
        }),

      menuItemViewed: (props) =>
        trackMenuItemViewed({
          ...props,
          currency: props.currency || currency,
        }),

      bannerClicked: trackBannerClicked,
      searchUsed: trackSearchUsed,
      languageChanged: trackLanguageChanged,

      // Cart
      itemAddedToCart: (props) =>
        trackItemAddedToCart({
          ...props,
          currency: props.currency || currency,
        }),

      itemRemovedFromCart: trackItemRemovedFromCart,

      cartViewed: (props) =>
        trackCartViewed({
          ...props,
          currency: props.currency || currency,
        }),

      // Checkout & Payment
      checkoutStarted: (props) =>
        trackCheckoutStarted({
          ...props,
        }),

      paymentSelected: trackPaymentSelected,

      orderSubmitted: (props) =>
        trackOrderSubmitted({
          ...props,
        }),

      orderPaymentSucceeded: (props) =>
        trackOrderPaymentSucceeded({
          ...props,
          currency: props.currency || currency,
        }),

      orderPaymentFailed: trackOrderPaymentFailed,

      // Order Tracking
      orderStatusViewed: trackOrderStatusViewed,
      taxiOrderTrackingOpened: trackTaxiOrderTrackingOpened,

      // External Services
      externalServiceCategoryViewed: trackExternalServiceCategoryViewed,
      externalServiceOrderSubmitted: (props) =>
        trackExternalServiceOrderSubmitted({
          ...props,
          currency: props.currency || currency,
        }),

      // Errors
      apiError: trackAPIError,
      paymentGatewayError: trackPaymentGatewayError,
    }),
    [track, currency]
  );

  return {
    track: trackingMethods,
    roomUuid,
    currency,
    // Direct access to mixpanel instance for advanced usage
    mixpanel,
  };
};

export default useMixpanel;
